import { ICruise, Market, Prices } from "../../store/slices/searchSlice";

export const hasAvailablePrice = (market: Market): boolean => {
  if (!market || typeof market !== "object") {
    return false;
  }

  return (
    Number(market.price) > 0 ||
    Number(market.single) > 0 ||
    Number(market.extra) > 0
  );
};

export const cruiseHasAvailablePrices = (cruise: ICruise): boolean => {
  return Object.values(cruise.markets)
    .filter((market): market is Market => {
      return (
        Boolean(market) && typeof market === "object" && !Array.isArray(market)
      );
    })
    .some(hasAvailablePrice);
};

export const getMarketPrice = (market: Market, subtractGft = false): number => {
  if (!market || typeof market !== "object") {
    return 0;
  }

  const prices = [
    Number(market.price),
    Number(market.single),
    Number(market.extra),
  ].filter((price) => !Number.isNaN(price) && price > 0);

  if (prices.length === 0) {
    return 0;
  }

  const minPrice = Math.min(...prices);
  const gft = subtractGft ? Number(market.gft || 0) : 0;

  return Math.max(0, minPrice - gft);
};

export const getCruiseMinPrice = (
  cruise: ICruise,
  subtractGft = false,
): number => {
  const prices = Object.values(cruise.markets)
    .filter((market): market is Market => {
      return (
        Boolean(market) && typeof market === "object" && !Array.isArray(market)
      );
    })
    .map((market) => getMarketPrice(market, subtractGft))
    .filter((price) => price > 0);

  return prices.length > 0 ? Math.min(...prices) : 0;
};

export const getMarketPrices = (
  market: Market,
  subtractGft = false,
): {
  price: number;
  single: number;
  extra: number;
  gft: number;
} => {
  if (!market || typeof market !== "object") {
    return { price: 0, single: 0, extra: 0, gft: 0 };
  }

  const gft = Number(market.gft || 0);
  const adjustment = subtractGft ? gft : 0;

  return {
    price: Math.max(0, Number(market.price || 0) - adjustment),
    single: Math.max(0, Number(market.single || 0) - adjustment),
    extra: Math.max(0, Number(market.extra || 0) - adjustment),
    gft,
  };
};

export const getPricesByGuestType = (
  prices?: Prices,
): {
  single: number;
  double: number;
  triple: number;
  quadruple: number;
} => {
  if (!prices) {
    return { single: 0, double: 0, triple: 0, quadruple: 0 };
  }

  const defaultSingle = Number(prices.single || 0);
  const defaultDouble = Number(prices.standard || 0);
  const defaultTriple = Number(prices.third || prices.extra || 0);
  const defaultQuadruple = Number(prices.fourth || prices.extra || 0);

  const single = defaultSingle > 0 ? Math.floor(defaultSingle) : 0;
  const double = defaultDouble > 0 ? Math.floor(defaultDouble) : 0;
  const triple =
    defaultTriple > 0 ? Math.floor((defaultDouble * 2 + defaultTriple) / 3) : 0;
  const quadruple =
    defaultQuadruple > 0
      ? Math.floor((defaultDouble * 2 + defaultTriple + defaultQuadruple) / 4)
      : 0;

  return { single, double, triple, quadruple };
};

export const isMarketAvailable = (
  marketKey: keyof ICruise["markets"],
  availableGrades: string[],
  cruise?: ICruise,
): boolean => {
  const market = cruise?.markets[marketKey] as Market;

  if (!market) {
    return false;
  }

  const hasPrice = hasAvailablePrice(market);
  const hasGrades = availableGrades.includes(marketKey);

  return hasPrice && hasGrades;
};

export const getDisplayPrice = (
  market: Market,
  subtractGft = false,
): string | "N/A" => {
  if (!hasAvailablePrice(market)) {
    return "N/A";
  }

  const price = getMarketPrice(market, subtractGft);

  return price > 0 ? price.toString() : "N/A";
};
